import { useEffect, useState } from 'react';

import ContentstackLivePreview from '@contentstack/live-preview-utils';
import Personalize from '@contentstack/personalize-edge-sdk';

import { fetchLivePreviewData } from 'services/livePreview';
import { Logger } from 'services/logger';
import { fetchPersonalizeData } from 'services/personalize';

import { ensureDefined } from 'utils/assert';
import { GATSBY_SOURCE_CONTENTSTACK_PREFIX } from 'utils/constants';
import { aliasToShortUid } from 'utils/contentstack/personalize';
import { contentstackLivePreviewSdk } from 'utils/contentstackLivePreviewSdk';
import { isLivePreviewEnabled, isPersonalizeEnabled } from 'utils/featureFlags';
import { dataLayerPush } from 'utils/functions';
import { convertKeysToCamelCase, getLivePreviewParams } from 'utils/livePreviewUtils';

interface PublishDetails {
  user: string;
  variants?: {
    [key: string]: {
      alias: string;
    };
  };
}

export interface BasePageData {
  pageType?: {
    title: string;
  }[];
  uid: string;
  typename: string;
  cslp__meta: Record<string, any>;
  publishDetails?: PublishDetails;
}

let sdkInitializationPromise: Promise<void> | null = null;
const initPersonalizeSDK = async () => {
  if (sdkInitializationPromise) {
    return sdkInitializationPromise;
  }

  sdkInitializationPromise = (async () => {
    await Personalize.init(ensureDefined(process.env.GATSBY_CONTENTSTACK_PERSONALIZE_UID));
    window.personalize = Personalize;
  })();

  return sdkInitializationPromise;
};

export const useContentstackData = <T extends BasePageData>(pageData: T) => {
  const [data, setData] = useState<T>(pageData);

  const loadLivePreviewData = async () => {
    try {
      const { hash, timestamp } = getLivePreviewParams();

      const livePreviewData = await fetchLivePreviewData({
        hash,
        timestamp,
        refs: data.cslp__meta?.referencePaths,
        pageUid: data.uid,
        pageType: data.typename?.replace(GATSBY_SOURCE_CONTENTSTACK_PREFIX, ''),
      });

      if (!livePreviewData) {
        return;
      }

      setData(convertKeysToCamelCase(livePreviewData));
    } catch (err) {
      Logger.error('Error fetching live preview data: ', err);
    }
  };

  const loadPersonalizeData = async () => {
    try {
      const personalizedData = await fetchPersonalizeData<T>(pageData.typename, pageData.uid);

      if (!personalizedData.publishDetails?.variants) {
        return;
      }

      // trigger impression based on the data.publish_Details.variant field existence
      setData(personalizedData);
      for (const variant of Object.values(personalizedData.publishDetails.variants)) {
        const variantShortUid = aliasToShortUid(variant.alias);
        await Personalize.triggerImpression(variantShortUid);
        dataLayerPush('ab_test_variation_viewed', 'event', {
          event: 'ab_test_variation_viewed',
          variation_alias: variant.alias,
          variation_id: variantShortUid,
          active_variant: Personalize.getVariants()[variantShortUid],
        });
        Logger.log('Trigger Personalize impression: ', aliasToShortUid(variant.alias));
      }
    } catch (err) {
      Logger.error('Error initializing Personalize: ', err);
      return false;
    }
  };

  useEffect(() => {
    if (isLivePreviewEnabled) {
      ContentstackLivePreview?.onEntryChange?.(loadLivePreviewData, {
        skipInitialRender: !contentstackLivePreviewSdk?.timelineConfig,
      });
    }

    if (isPersonalizeEnabled) {
      initPersonalizeSDK().then(() => loadPersonalizeData());
    }
  }, []);

  return data;
};
